import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, type LanguageCode } from './languages';

export interface InitI18nOptions {
  /** Namespaces to load for this app */
  ns: string[];
  /** Default namespace */
  defaultNS: string;
  /** Path to locale files, defaults to /locales/{{lng}}/{{ns}}.json */
  localePath?: string;
}

/**
 * Initialize i18next for an OM app.
 * Call this once at app entry before rendering.
 */
export function initI18n({ ns, defaultNS, localePath = '/locales/{{lng}}/{{ns}}.json' }: InitI18nOptions) {
  if (i18n.isInitialized) {
    return i18n;
  }

  i18n
    .use(HttpBackend)
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
      nonExplicitSupportedLngs: true,
      load: 'languageOnly',
      fallbackLng: DEFAULT_LANGUAGE,
      defaultNS,
      ns,
      interpolation: {
        escapeValue: false, // React handles escaping
      },
      detection: {
        // Check localStorage first (persisted user choice), then browser language
        order: ['localStorage', 'navigator'],
        lookupLocalStorage: 'om_language',
        caches: ['localStorage'],
      },
      backend: {
        loadPath: localePath,
      },
      // React suspense mode: wait for translations before rendering
      react: {
        useSuspense: true,
      },
    });

  return i18n;
}

/**
 * Change the active language and persist to localStorage.
 */
export function changeLanguage(code: LanguageCode): Promise<void> {
  return i18n.changeLanguage(code) as unknown as Promise<void>;
}

/**
 * Load a font for a non-Latin script lazily to avoid penalizing English users.
 * Safe to call multiple times — checks for existing <link> tags.
 */
export function loadScriptFont(fontFamily: string | null): void {
  if (!fontFamily) return;

  const id = `om-font-${fontFamily.replace(/\s+/g, '-').toLowerCase()}`;
  if (document.getElementById(id)) return;

  const params = new URLSearchParams({
    family: `${fontFamily}:wght@400;500;600;700`,
    display: 'swap',
  });

  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?${params.toString()}`;
  document.head.appendChild(link);
}

export { i18n };
